// Change Analysis panel (spec §8.7). Renders the pure analysis object from
// engine/change-analysis.js — it computes, this file only escapes and lays out.
//
// Layout: two columns on desktop, stacked below 860px. LEFT is the measurement —
// field-frequency bars, then the record distribution. RIGHT is what to read and
// what can be done — findings, unchanged fields, the collapsed ignore controls,
// and the counting rules behind all of it. Findings carry the visual weight;
// explanations sit under them at a smaller size; monospace is reserved for field
// names and values, so a field name reads as data rather than as prose.
//
// Frequency is stated ONCE, as the bars. The ignore section is only the action.
//
// Every user-derived string (field name, type name) passes through esc(). The
// analysis module returns those raw on purpose, so escaping happens exactly once,
// here, at the HTML boundary. Colour is never the only channel: the distribution
// carries its full breakdown as an aria-label and repeats the shares as text.
import { esc } from './escape.js';
import { percentText } from '../engine/change-analysis.js';

const CHIP_CAP = 12; // unchanged-field chips shown before a count line takes over
const BAR_CAP = 6; // frequency bars shown before "Show all"
const num = (n) => n.toLocaleString('en-US');

export function analysisHtml(a) {
  if (!a) return '';
  return `${applied(a)}<div class="an-grid">
    <section class="an-col">${fieldBars(a)}${distribution(a)}</section>
    <section class="an-col">${findings(a)}${untouched(a)}${ignoreSection(a)}${schema(a)}${howCalculated(a)}</section>
  </div>`;
}

// The single line the header carries: how much of the data is affected — or, once
// an ignore rule has dropped the comparison below threshold, the before/after that
// spec §8.7 asks for in place of the panel disappearing under the cursor.
export function analysisLead(a, baseline = null) {
  if (!a) return '';
  if (baseline !== null && baseline !== a.differences) {
    return `${num(baseline)} → ${num(a.differences)} differences under the current ignore rules`;
  }
  return a.headline || '';
}

/* ---------- state: what the comparison is currently ignoring ---------- */

// Full width, above both columns, never behind a disclosure: an ignored field
// leaves no trace in the diff, so this band is the only thing telling a reader why
// the numbers dropped — and the only way back.
function applied(a) {
  if (!a.ignored?.length) return '';
  const chips = a.ignored
    .map(
      (f) =>
        `<li><code>${esc(f)}</code><button type="button" class="an-undo" data-unignore-field="${esc(f)}" aria-label="Stop ignoring ${esc(f)} and re-run the comparison">Undo</button></li>`,
    )
    .join('');
  return `<div class="an-applied"><span class="an-applied-label">Ignoring</span><ul class="an-chips">${chips}</ul></div>`;
}

/* ---------- left column: the measurement ---------- */

function fieldBars(a) {
  if (!a.fields.length) {
    return a.records.modified
      ? ''
      : '<h4 class="an-h">Field changes</h4><p class="an-sub">No field edits — every affected record was added or removed whole.</p>';
  }
  const bar = (f) => {
    const share = percentText(f.changed, a.modifiedRecords);
    return `<li><code class="an-bar-name">${esc(f.name)}</code>
      <span class="an-bar" role="img" aria-label="${num(f.changed)} of ${num(a.modifiedRecords)} modified records, ${share}"><span class="an-bar-fill" style="width:${(f.changed / a.modifiedRecords) * 100}%"></span></span>
      <span class="an-bar-val">${num(f.changed)}/${num(a.modifiedRecords)} · ${share}</span></li>`;
  };
  const shown = a.fields.slice(0, BAR_CAP);
  const rest = a.fields.slice(BAR_CAP);
  const more = rest.length
    ? `<details class="an-more"><summary>Show all ${num(a.fields.length)} changed fields</summary><ul class="an-bars">${rest.map(bar).join('')}</ul></details>`
    : '';
  return `<h4 class="an-h">Field changes</h4>
    <p class="an-sub">Frequency among the ${num(a.modifiedRecords)} modified records</p>
    <ul class="an-bars">${shown.map(bar).join('')}</ul>${more}`;
}

const SEGMENTS = [
  { key: 'added', cls: 'add', label: 'added' },
  { key: 'removed', cls: 'rm', label: 'removed' },
  { key: 'modified', cls: 'ch', label: 'modified' },
  { key: 'unchanged', cls: 'un', label: 'unchanged' },
];

function distribution(a) {
  const r = a.records;
  if (!r.total) return '';
  const present = SEGMENTS.filter((s) => r[s.key] > 0);
  const text = present.map((s) => `${num(r[s.key])} ${s.label} (${percentText(r[s.key], r.total)})`).join(', ');
  // Widths stay strictly proportional. A 0.01% slice is sub-pixel, and that is the
  // truth of it — padding it out to a visible minimum would draw a rare change as
  // a common one. The share is carried in text and in the label instead.
  const bars = present
    .map((s) => `<span class="an-seg ${s.cls}" style="width:${(r[s.key] / r.total) * 100}%"></span>`)
    .join('');
  return `<h4 class="an-h">Record distribution</h4>
    <div class="an-dist" role="img" aria-label="Record distribution: ${esc(text)}">${bars}</div>
    <p class="an-dist-key"><b>${num(r.touched)} affected</b> <span class="an-strong">${percentText(r.touched, r.total)}</span> · ${num(r.unchanged)} unchanged · of ${num(r.total)} records compared</p>`;
}

/* ---------- right column: what to read, and what can be done ---------- */

// Emphasis comes from the analysis telling us which segment is which — a count,
// a field name, or plain text. Matching those back out of a finished sentence
// would mean running a regex over user-controlled field names.
function findings(a) {
  const rich = a.summaryRich || a.summary.map((t) => [{ t }]);
  if (!rich.length) return '';
  const seg = (p) =>
    p.k === 'field' ? `<code>${esc(p.t)}</code>` : p.k === 'strong' ? `<b>${esc(p.t)}</b>` : esc(p.t);
  return `<h4 class="an-h">Findings</h4><ul class="an-findings">${rich
    .map((parts) => `<li>${parts.map(seg).join('')}</li>`)
    .join('')}</ul>`;
}

function untouched(a) {
  if (!a.comparableFields || !a.untouchedFields.length) return '';
  const shown = a.untouchedFields.slice(0, CHIP_CAP);
  const rest = a.untouchedFields.length - shown.length;
  const more = rest > 0 ? `<li class="an-chip-more">+${num(rest)} more</li>` : '';
  return `<h4 class="an-h">Unchanged across matched records</h4>
    <ul class="an-chips">${shown.map((f) => `<li><code>${esc(f)}</code></li>`).join('')}${more}</ul>`;
}

// Frequency is shown once, as bars in the left column. This section is only the
// ACTION: collapsed, counted in its heading, each button beside the effect it
// produces, and the scope of the rule stated before anyone reaches for it.
function ignoreSection(a) {
  if (!a.suggestions?.length) return '';
  const rows = a.suggestions
    .map((s) => {
      const action = s.ignorable
        ? `<button type="button" class="an-act" data-ignore-field="${esc(s.field)}" aria-label="Ignore ${esc(s.field)} and re-run the comparison">Ignore ${esc(s.field)}</button>`
        : `<span class="an-sub"><code>${esc(s.field)}</code> — this name has a comma, a line break or edge spaces, which the Ignore fields rule cannot hold</span>`;
      return `<li>${action}<span class="an-sub">${esc(s.effect)}</span></li>`;
    })
    .join('');
  // Clicking the rows one at a time cannot reach the combined result — ignoring
  // the first leaves the second as the only changed field, where the contrast rule
  // stops listing it (REL-067) — so the combined outcome needs its own control.
  const all = a.projectedAll
    ? `<li><button type="button" class="an-act" data-ignore-all="${esc(a.projectedAll.fields.join(','))}" aria-label="Ignore all ${a.projectedAll.fields.length} fields and re-run the comparison">${a.projectedAll.fields.length === 2 ? 'Ignore both fields' : `Ignore all ${a.projectedAll.fields.length} fields`}</button><span class="an-sub">${esc(a.projectedAll.effect)}</span></li>`
    : '';
  return `<details class="an-detail"><summary>Ignore fields (${num(a.suggestions.length)})</summary>
    <p class="an-sub">Listed for changing in more than 80% of the modified records — frequency, not a judgement about it. Ignores this field name at any depth. Re-runs the comparison; undo stays available above.</p>
    <ul class="an-acts">${rows}${all}</ul></details>`;
}

function schema(a) {
  const { added, removed, typeChanges } = a.schema;
  const n = added.length + removed.length + typeChanges.length;
  if (!n) return '';
  const chips = [
    ...added.map((x) => `<li><span class="an-sw add" aria-hidden="true"></span><code>${esc(x)}</code> added</li>`),
    ...removed.map((x) => `<li><span class="an-sw rm" aria-hidden="true"></span><code>${esc(x)}</code> removed</li>`),
    ...typeChanges.map(
      (t) => `<li><span class="an-sw ch" aria-hidden="true"></span><code>${esc(t.name)}</code> ${esc(String(t.oldType))} → ${esc(String(t.newType))}</li>`,
    ),
  ].join('');
  return `<details class="an-detail"><summary>Schema changes (${num(n)})</summary>
    <p class="an-sub">Column-level edits, counted once each and kept out of the per-record numbers. A declared type change is an observation, not a verdict on whether a consumer breaks.</p>
    <ul class="an-chips an-schema">${chips}</ul></details>`;
}

// The counting rules live here rather than beside every number: they are what a
// reader checks once when a figure looks wrong, not something to re-read each time.
function howCalculated(a) {
  const rules = [
    'A record counts once, however many of its fields changed.',
    'The affected share divides by every record on either side — added, removed, modified and unchanged.',
    `Field frequency divides by the ${num(a.modifiedRecords)} modified records only; added and removed records are whole-record changes and contribute no field edits.`,
    `Unchanged fields are those present on both sides${a.keyColumn ? `, excluding the key (${esc(a.keyColumn)})` : ''} and not currently ignored — an ignored field is out of scope, never reported as unchanged.`,
    'Row reordering and duplicate-key warnings are notes about the comparison, not differences.',
  ];
  return `<details class="an-detail"><summary>How this is calculated</summary>
    <ul class="an-rules">${rules.map((r) => `<li>${r}</li>`).join('')}</ul></details>`;
}
